import { BaseComponentImpl } from '../../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate, getLifetimeForDirection, getIntervalForDirection } from '../../TemplatesEditor/types';
import { computeShapeArea } from '../../../../types/GeometryShapes';
import { MaintenanceIntervalType } from '../../../../types/Enums';
import { Building } from '../../../../types/protocols';
import { resolveCardinalDirection } from './cardinalDirectionResolver';
import { MS_PER_YEAR, isConditionSet } from '../../../../Helpers/conditionUtils';

// Re-export for backwards compatibility
export { calculateCurrentCondition } from '../../../../Helpers/conditionUtils';

/**
 * Calculate component area from area shapes
 */
export const calculateComponentArea = (component: BaseComponentImpl): number => {
    if (!component.areaShapes?.value || !Array.isArray(component.areaShapes.value)) {
        return 0;
    }
    
    return component.areaShapes.value.reduce((total, shape) => {
        return total + computeShapeArea(shape);
    }, 0);
};

/**
 * Calculate suggested replacement cost with explanation
 */
export const calculateSuggestedReplacementCost = (
    component: BaseComponentImpl, 
    lifetime: ComponentLifetime | null,
    building: Building | null
): { cost: number; calculation: string } => {
    if (!lifetime) {
        return { cost: 0, calculation: '' };
    }

    // Get the appropriate lifetime data (considering directional lifetimes)
    const cardinalDirection = resolveCardinalDirection(component, building);
    const directionalLifetime = getLifetimeForDirection(lifetime, cardinalDirection);

    if (!directionalLifetime.replacementCost) {
        return { cost: 0, calculation: '' };
    }

    if (lifetime.costUnit === 'per_sqm') {
        const area = calculateComponentArea(component);

        if (area === 0) {
            return {
                cost: 0,
                calculation: 'Antal kvadratmeter saknas för komponent'
            };
        }
        const totalCost = Math.round(directionalLifetime.replacementCost * area);
        return {
            cost: totalCost,
            calculation: `${directionalLifetime.replacementCost} kr per kvm * ${area} kvm`
        };
    } else if (lifetime.costUnit === 'per_lm') {
        const length = component.linearMeters?.value;

        if (!length) {
            return {
                cost: 0,
                calculation: 'Antal löpmeter saknas för komponent'
            };
        }
        const totalCost = Math.round(directionalLifetime.replacementCost * length);
        return {
            cost: totalCost,
            calculation: `${directionalLifetime.replacementCost} kr per lm * ${length} lm`
        };
    } else {
        return {
            cost: directionalLifetime.replacementCost,
            calculation: `${directionalLifetime.replacementCost} kr per enhet`
        };
    }
};

/**
 * Calculate suggested maintenance cost with explanation
 */
export const calculateSuggestedMaintenanceCost = (
    component: BaseComponentImpl,
    template: MaintenanceTemplate
): { cost: number; calculation: string } => {
    if (!template.cost) {
        return { cost: 0, calculation: '' };
    }

    if (template.cost_unit === 'per_sqm') {
        const area = calculateComponentArea(component);

        if (area === 0) {
            return {
                cost: 0,
                calculation: 'Antal kvadratmeter saknas för komponent'
            };
        }
        const totalCost = Math.round(template.cost * area);
        return {
            cost: totalCost,
            calculation: `${template.cost} kr per kvm * ${area} kvm`
        };
    } else if (template.cost_unit === 'per_lm') {
        const length = component.linearMeters?.value;

        if (!length) {
            return {
                cost: 0,
                calculation: 'Antal löpmeter saknas för komponent'
            };
        }
        const totalCost = Math.round(template.cost * length);
        return {
            cost: totalCost,
            calculation: `${template.cost} kr per lm * ${length} lm`
        };
    } else {
        return {
            cost: template.cost,
            calculation: `${template.cost} kr per enhet`
        };
    }
};

/**
 * Calculate replacement date based on condition
 */
export const calculateReplacementDate = (
    component: BaseComponentImpl, 
    lifetime: ComponentLifetime,
    building: Building | null
): Date | null => {
    const condition = component.condition?.value?.value;
    if (!isConditionSet(condition) || !component.condition?.updatedWhen || !lifetime.expectedLifetimeYears) {
        return null;
    }

    const assessmentDate = new Date(component.condition.updatedWhen);
    
    // Use directional lifetime if available
    const cardinalDirection = resolveCardinalDirection(component, building);
    const directionalLifetime = getLifetimeForDirection(lifetime, cardinalDirection);
    const expectedLifetime = directionalLifetime.expectedLifetimeYears;

    // Calculate years remaining based on condition (10 = full lifetime, 0 = replace now)
    const yearsRemainingFromCondition = (condition / 10) * expectedLifetime;

    // Calculate years since assessment
    const now = new Date();
    const yearsSinceAssessment = (now.getTime() - assessmentDate.getTime()) / MS_PER_YEAR;

    // Adjust for time passed since assessment
    const adjustedYearsRemaining = yearsRemainingFromCondition - yearsSinceAssessment;

    const replacementDate = new Date();
    replacementDate.setFullYear(replacementDate.getFullYear() + Math.max(0, adjustedYearsRemaining));

    return replacementDate;
};

/**
 * Calculate years remaining for a component
 */
export const calculateYearsRemaining = (
    component: BaseComponentImpl, 
    lifetime: ComponentLifetime,
    building: Building | null
): number | null => {
    const condition = component.condition?.value?.value;
    if (!isConditionSet(condition) || !lifetime.expectedLifetimeYears) {
        return null;
    }

    
    // Use directional lifetime if available
    const cardinalDirection = resolveCardinalDirection(component, building);
    const directionalLifetime = getLifetimeForDirection(lifetime, cardinalDirection);
    const expectedLifetime = directionalLifetime.expectedLifetimeYears;

    // Calculate years remaining based on condition (10 = full lifetime, 0 = replace now)
    const yearsRemainingFromCondition = (condition / 10) * expectedLifetime;

    // Adjust for time passed since assessment
    if (component.condition?.updatedWhen) {
        const assessmentDate = new Date(component.condition.updatedWhen);
        const now = new Date();
        const yearsSinceAssessment = (now.getTime() - assessmentDate.getTime()) / MS_PER_YEAR;
        return Math.max(0, yearsRemainingFromCondition - yearsSinceAssessment);
    }

    return yearsRemainingFromCondition;
};

/**
 * Calculate projected install date based on condition and lifetime
 */
export const calculateInstallDate = (
    component: BaseComponentImpl, 
    lifetime: ComponentLifetime,
    building: Building | null
): Date | null => {
    const condition = component.condition?.value?.value;
    if (!isConditionSet(condition) || !component.condition?.updatedWhen || !lifetime.expectedLifetimeYears) {
        return null;
    }

    const assessmentDate = new Date(component.condition.updatedWhen);
    
    // Use directional lifetime if available
    const cardinalDirection = resolveCardinalDirection(component, building);
    const directionalLifetime = getLifetimeForDirection(lifetime, cardinalDirection);
    const expectedLifetime = directionalLifetime.expectedLifetimeYears;

    // Calculate how much life has been used based on condition (10 = new, 0 = end of life)
    const lifeUsedPercent = (10 - condition) / 10;
    const yearsUsed = lifeUsedPercent * expectedLifetime;

    // Calculate install date by going back from assessment date
    const installDate = new Date(assessmentDate);
    installDate.setFullYear(installDate.getFullYear() - yearsUsed);

    return installDate;
};

/**
 * Calculate next maintenance due date based on condition progression
 */
export const calculateNextMaintenanceDate = (
    component: BaseComponentImpl, 
    template: MaintenanceTemplate, 
    lifetime: ComponentLifetime,
    building: Building | null
): Date | null => {
    const condition = component.condition?.value?.value;
    if (!isConditionSet(condition) || !component.condition?.updatedWhen || !lifetime.expectedLifetimeYears) {
        return null;
    }

    const expectedLifetime = lifetime.expectedLifetimeYears;
    // Use directional interval if available
    const cardinalDirection = resolveCardinalDirection(component, building);
    const intervalData = getIntervalForDirection(template, cardinalDirection);
    const maintenanceIntervalYears = intervalData.interval_type === MaintenanceIntervalType.years 
        ? intervalData.interval_value 
        : intervalData.interval_value / 365; // Convert days to years for calculation

    // Calculate how much of the lifetime has passed based on condition
    // Condition 10 = 0% passed, Condition 0 = 100% passed
    const lifetimePassedPercent = (10 - condition) / 10;
    const yearsPassed = lifetimePassedPercent * expectedLifetime;

    // Calculate when the last maintenance should have been done
    const lastMaintenanceYear = Math.floor(yearsPassed / maintenanceIntervalYears) * maintenanceIntervalYears;
    
    // Calculate next maintenance due date
    const nextMaintenanceYear = lastMaintenanceYear + maintenanceIntervalYears;
    const yearsUntilNextMaintenance = nextMaintenanceYear - yearsPassed;

    // Create date based on assessment date + years until next maintenance
    const assessmentDate = new Date(component.condition.updatedWhen);
    const nextMaintenanceDate = new Date(assessmentDate);
    nextMaintenanceDate.setFullYear(assessmentDate.getFullYear() + yearsUntilNextMaintenance);

    return nextMaintenanceDate;
};
